import { Button, Card, Col, Collapse, Row } from "react-bootstrap";
import { StateWithCountryLookupItem } from "../../../Shared/Models/StateWithCountryLookupItem";
import Physician from "../../../Physician/Models/Physician";
import { MedicalLicense } from "../../../Physician/Models/MedicalLicense";
import { Fragment, useEffect, useState } from "react";
import { deepCopy } from "../../../Shared/Utility/DeepCopy";
import MedicalLicensure from "../../ContinuingCertification/Components/MedicalLicensure";
import { Activities } from "./Activities";
import { Certificate, CertificateStatus, CertificationGeneration } from "../../../Physician/Models/Certificate";
import { getDateWithOffset } from "../../../Shared/Utility/GetDateWithOffset";
import React from "react";

interface IProps {
    states: Array<StateWithCountryLookupItem>;
    physician: Physician;
    updatePhysician: (physician: Physician) => void;
}

export function CertificationRequirements(props: IProps) {
    let requiredArticles = (props.physician.hasActiveTenYearCert && !props.physician.ccEnrollmentsModel.hasCCEnrollmentHistory) ? 30 : 90;
    const [openCollapse, setOpenCollapse] = useState<boolean>(false);

    useEffect(() => {
    }, []);

    const onSaveLicenses = (licenses: Array<MedicalLicense>) => {
        var updatedPhysician = deepCopy(props.physician);
        updatedPhysician.medicalLicenses = licenses;

        props.updatePhysician(updatedPhysician);
    }

    const sortBySpecialtyAlpha = (a: Certificate, b: Certificate) => {
        //sort by Specialty/Subspecialty
        if (a.isPrimary === b.isPrimary) {
            //then by alpha
            return a.certificationName < b.certificationName ? -1 : 1
        } else {
            return a.isPrimary ? -1 : 1;
        }
    }

    const getCertsString = (certs: Certificate[], expiredOnly: boolean = true) => {
        let certList = certs;
        if (expiredOnly) {
            certList = certList.filter(_ => _.certificateStatus === CertificateStatus.Inactive);
        }
        let sortedExpiredCerts = certList.sort(sortBySpecialtyAlpha).map(_ => _.certificationName);

        if (sortedExpiredCerts.length > 1) {
            return sortedExpiredCerts.slice(0, -1).join(", ").
                concat(", and ", sortedExpiredCerts[sortedExpiredCerts.length - 1], " certifications");
        } else if (sortedExpiredCerts.length === 1) {
            return sortedExpiredCerts[0].concat(" certification");
        } else {
            return "";
        }
    }

    const displayExpiredReasonText = () => {
        if(props.physician.isLapsedNoLicense)
            return "not meeting ABPN certification program requirements.";
        else
            return "not meeting certification requirements within two 3-year blocks (6 years).";
    }

    const displayExpiredOrUnenrollmentTextForLifetimeCertHolders = () => {
        if(props.physician.isLapsedNoLicense)
            return `Your ${getCertsString(props.physician.certificates, false)} expired due to not meeting ABPN certification program requirements.`;
        else
            return `You are no longer enrolled in the Continuing Certification (CC) program for your ${getCertsString(props.physician.certificates, false)} due to not completing the CC requirements for one three-year block (three years).`;
    }

    const displayRecertificationInstructions = () => {
        return (
            <Fragment>
                To regain certification, you will need to do the following:
                <br /><br />
                1.  Update your medical license(s) information on file. Medical license(s) must be active, full, and unrestricted.
                <br /><br />
                2.  Complete one set of activity requirements over the past {yearsRequirement} years:<br />
                <ul>
                    <li>{requiredCredits} Category 1 CME credits (includes SA credit)</li>
                    <li>16 Self-Assessment (SA) CME credits</li>
                    <li>1 Performance in Practice (PIP) activity</li>
                    {(showPatientSafetyRequirement || isInactive) && <li>1 Patient Safety activity (if applicable)</li>}
                    <li><strong>Diplomates will be</strong> required to submit documentation of completed activity requirements for auditing purposes.</li>
                </ul>
                For more information about <a target="_blank" href={"https://www.abpn.org/maintain-certification/moc-activity-requirements/"}>
                    Activity Requirements
                </a>, please visit our website.
                <br /><br />
                3.  Apply and pay for the CC/recertification exam application OR participate in the Article Based Continuing Certification (ABCC) pathway for each certification you wish to recertify in.*  Payment for recertification is due at time of application. If audited, diplomates will need to submit documentation (CME certificates, transcripts, etc.) of the completed activity requirements. For more information about the ABCC pathway, please visit&nbsp;
                <a target="_blank" href={"https://www.abpn.org/maintain-certification/article-based-continuing-certification-abcc-pathway/"}>
                    abpn.org
                </a>.
                <br />
                *Diplomates must maintain primary certification for dependent subspecialty certifications.
                <br /><br />
                <div>
                    <p>Diplomates who have lapsed with their initial certification will be recertified upon passing the CC exam(s) OR successful completion of 60 ABCC article exams.* Diplomates with a lapsed specialty certificate (Psychiatry, Neurology, or Child Neurology) can participate in the ABCC Pathway in lieu of taking the traditional recertification exam. There is a three (3) year waiting period for recertification using the ABCC assessment pathway.</p> 
                    <p>If a diplomate is currently in the CC Program with an active initial certification or has an active Lifetime Certification but has a lapsed subspecialty certificate, they may regain certification for the lapsed certificate by submitting an article assessment application and recertification re-entry fee, and successful completion of 20 article exams by December 15th of the year of applying for the lapsed certification.</p>
                </div>
                
                <br /><br />
                <i>
                    *ABPN diplomates who hold subspecialty certification administered by another ABMS Member Board are not eligible for the ABPN ABCC pathway for that certification. For more information about recertification options, <a href="https://abpn.org/faq/multi-disciplinary-specialties-mds/">please contact the respective administrating ABMS member Board</a>.
                </i>
                {hasLiftimeCert &&
                    <div>
                        <br /><br />
                        <p><a href="https://abpn.org/faqs/">See the ABPN website FAQs on ABCC</a></p>
                        <p><a href="https://abpn.org/maintain-certification/">Visit the ABPN website for all details on the Continuing Certification Program</a></p>
                        <br /><br />
                    </div>
                }
            </Fragment>
        );
    }

    let requiredCredits = 90;
    let showRecertRequirements = true;
    let showLifetimeOnlyLanguage = false;
    let showLifetimeOnlyTenYearLanguage = false;
    let today = new Date();

    let yearsRequirement = "three (3)";
    //Lifetime Only
    if (props.physician.certificates.every(_ => _.certificationGeneration == CertificationGeneration.Lifetime)) {
        if (props.physician.ccEnrollmentsModel.hasCCEnrollmentHistory) {
            showLifetimeOnlyLanguage = true;
        } else if ((props.physician.certificates.every(_ => !_.recertifiedDate ||
            (getDateWithOffset(_.recertifiedDate).getTime() < getDateWithOffset(new Date(2012, 1, 1).toString()).getTime() && (!_.expirationDate || today.getTime() > getDateWithOffset(_.expirationDate).getTime())))
        )) {
            showRecertRequirements = false;
        } else if (props.physician.certificates.every(_ => _.recertifiedDate != null)) {
            if (props.physician.certificates.some(_ => today.getTime() > getDateWithOffset(_.expirationDate).getTime())) {
                showRecertRequirements = false;
            } else {
                //Active 10 year on the Lifetime cert
                showLifetimeOnlyTenYearLanguage = true;
                requiredCredits = 300;
                requiredArticles = 30;
                yearsRequirement = "three";
            }
        }
    }


    let hasTenYearCert = false;
    if (props.physician.certificates.some(_ => _.certificationGeneration == CertificationGeneration.TenYear)) {
        hasTenYearCert = true;
        yearsRequirement = "three";
    }

    if (props.physician.certificates.every(_ => _.certificationGeneration == CertificationGeneration.TenYear)) {
        requiredCredits = 300;
    }

    let isInactive = false;
    if (props.physician.certificates.every(_ =>  _.certificateStatus == CertificateStatus.Inactive)) {
        isInactive = true;
        requiredCredits = 90;
        yearsRequirement = "three";
    }

    let hasLiftimeCert = false;
    if (props.physician.certificates.some(_ => _.certificationGeneration == CertificationGeneration.Lifetime)) {
        hasLiftimeCert = true;
    }

    let showPatientSafetyRequirement = true;
    if (props.physician.certificates.every(_ => _.certificationGeneration == CertificationGeneration.TenYear)) {
        showPatientSafetyRequirement = false;
    }

    return (
        <div>
            {(showRecertRequirements && !props.physician.isDansInvalidatedAllowReentryExam) &&
                <Card className="mb-4" border="pp-secondary-grey">
                    <Card.Body>
                        <Card.Title className="mb-0 font-weight-bolder">
                            <span className="text-pp-primary">Recertification Requirements 
                                <Button onClick={() => setOpenCollapse(!openCollapse)} size="sm" variant="outline-pp-primary rounded-0" className="float-right">{openCollapse ? "Hide" : "Show"} Details</Button>
                            </span>
                        </Card.Title>
                        <Row>
                            <Col xs={14}>
                                <div className="text-pp-primary font-weight-bolder">Important message regarding your certification</div>
                            </Col>
                        </Row>
                        <Collapse in={openCollapse}>
                            <Row className="mt-3">
                                <Col xs={12}>
                                    <div className="pp-font-size-smaller">
                                        {showLifetimeOnlyLanguage ?
                                            <div>
                                                {displayExpiredOrUnenrollmentTextForLifetimeCertHolders()}  Lifetime certificates are not required to participate in CC.  The CC status of your lifetime certification(s) is now listed as ‘Not Required’.
                                                {!props.physician.isLapsedNoLicense &&
                                                <Fragment>
                                                    <br /><br />
                                                    Diplomates who wish to participate in CC for a non-time limited (lifetime) certification will need to enroll in the traditional recertification/CC Exam OR the Article Based Continuing Certification (ABCC) pathway. Diplomates will pay the first annual fee for that certification at the time of enrollment, and will then be listed as "Meeting" CC/MOC requirements. Diplomates will have the next three years to pass the recertification/CC exam OR successfully complete 20 article exams, and complete the CC requirements.
                                                    <br /><br />
                                                    Diplomates who choose the article pathway will need to continue to complete article exams each three- year block, in addition to satisfying the CC requirements. For more information about the ABCC pathway, please visit&nbsp;
                                                    <a target="_blank" href={"https://www.abpn.org/maintain-certification/article-based-continuing-certification-abcc-pathway/"}>
                                                        abpn.org
                                                    </a>.
                                                </Fragment>
                                                }
                                                <br /><br />
                                                {displayRecertificationInstructions()}
                                            </div>
                                            :
                                            <div>
                                                {props.physician.ccEnrollmentsModel.hasCCEnrollmentHistory &&
                                                    <span>
                                                        Your {getCertsString(props.physician.certificates.filter(_ => _.certificationGeneration == CertificationGeneration.Cc), true)} expired due to {displayExpiredReasonText()}<br /><br />
                                                        {hasLiftimeCert &&
                                                            <span>
                                                                You are no longer enrolled in the Continuing Certification (CC) program for your {getCertsString(props.physician.certificates.filter(_ => _.certificationGeneration == CertificationGeneration.Lifetime), false)} due to not meeting CC requirements for one three-year block (three years). Lifetime certificates are not required to participate in CC.  The CC status of your lifetime certification(s) is now listed as ‘Not Required’.<br /><br />
                                                            </span>
                                                        }
                                                    </span>
                                                }
                                                {showLifetimeOnlyTenYearLanguage &&
                                                    <span>
                                                        You are currently participating in the Continuing Certification program. Non-Time Limited (Lifetime) certificates are not required to participate in CC.   If you wish to maintain your Continuing Certification status, you will need to recertify.<br /><br />
                                                    </span>
                                                }
                                                {displayRecertificationInstructions()}
                                            </div>
                                        }
                                    </div>
                                </Col>
                            </Row>
                        </Collapse>
                    </Card.Body>
                </Card>}

            {!props.physician.isDansInvalidatedAllowReentryExam && <Activities /> }
            
            <MedicalLicensure
                medicalLicenses={props.physician.medicalLicenses}
                states={props.states}
                onSaveLicenses={(licenses: Array<MedicalLicense>) => onSaveLicenses(licenses)}
                isSectionOpen={true}
                showCollapse={false} />
        </div>
    )
}